import { useTranslation } from 'react-i18next';
import type {
  SchemeMechanic,
  ConditionGroup,
  Condition,
  PayoutDefinition,
  ExternalVerification,
} from '@infoscend-claims/shared';

// ── Inline SVG icon mini-set ─────────────────────────────────────────────────

export const SCHEME_ICONS: Record<string, string> = {
  CheckSquare: `<path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>`,
  TrendingUp: `<polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/>`,
  BarChart3: `<path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>`,
  ScanLine: `<path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><line x1="3" y1="12" x2="21" y2="12"/>`,
  PackagePlus: `<path d="M16 16h6"/><path d="M19 13v6"/><path d="m7.5 4.27 9 5.15"/><polyline points="3.29 7 12 12 20.71 7"/><line x1="12" y1="22" x2="12" y2="12"/>`,
  Receipt: `<path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/><path d="M12 17.5v-11"/>`,
  Minus: `<path d="M5 12h14"/>`,
  Trophy: `<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>`,
  Store: `<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/>`,
  Gift: `<polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>`,
  Zap: `<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>`,
  Award: `<circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>`,
  Clock: `<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>`,
  BarChart2: `<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>`,
};

export function SchemeIcon({ name, size = 20 }: { name: string; size?: number }) {
  const d = SCHEME_ICONS[name] ?? SCHEME_ICONS['Receipt']!;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: d }} />
  );
}

// ── Confidence badge ─────────────────────────────────────────────────────────

export function ConfidenceBadge({ confidence, label }: { confidence: number; label?: string }) {
  const { t } = useTranslation();
  if (confidence >= 0.9) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-success font-semibold">
        <span>✓</span>{label ?? t('admin.schemes.confidenceHigh', { defaultValue: 'High' })}
      </span>
    );
  }
  if (confidence >= 0.7) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 font-semibold">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
        {label ?? t('admin.schemes.confidenceMedium', { defaultValue: 'Medium' })}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-danger font-semibold">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-danger" />
      {label ?? t('admin.schemes.confidenceLow', { defaultValue: 'Low' })}
    </span>
  );
}

// ── Condition row editor ─────────────────────────────────────────────────────

export const CONDITION_TYPES = [
  'SKU_PRESENCE', 'DISPLAY_EVIDENCE', 'DOCUMENT_MATCH', 'MIN_FACINGS',
  'DURATION_HOURS', 'LISTING_WEEKS', 'VOLUME_THRESHOLD', 'RETAILER_SIGNATURE_PRESENT', 'CUSTOM_PREDICATE',
] as const;

export function ConditionRow({ cond, onChange, onRemove }: {
  cond: Condition;
  onChange: (c: Condition) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const inputCls = 'border border-hairline rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-copper';

  return (
    <div className="flex items-start gap-2 bg-bone border border-hairline rounded p-2">
      <select
        className={`${inputCls} w-44 shrink-0`}
        value={cond.type}
        onChange={(e) => {
          const ty = e.target.value as Condition['type'];
          if (ty === 'SKU_PRESENCE') onChange({ type: 'SKU_PRESENCE', targetSkus: [] });
          else if (ty === 'DISPLAY_EVIDENCE') onChange({ type: 'DISPLAY_EVIDENCE', evidenceType: 'PHOTO' });
          else if (ty === 'DOCUMENT_MATCH') onChange({ type: 'DOCUMENT_MATCH', docType: 'INVOICE' });
          else if (ty === 'MIN_FACINGS') onChange({ type: 'MIN_FACINGS', count: 4 });
          else if (ty === 'DURATION_HOURS') onChange({ type: 'DURATION_HOURS', minHours: 2 });
          else if (ty === 'LISTING_WEEKS') onChange({ type: 'LISTING_WEEKS', minWeeks: 4 });
          else if (ty === 'VOLUME_THRESHOLD') onChange({ type: 'VOLUME_THRESHOLD', minVolume: 100, uom: 'CASES' });
          else if (ty === 'RETAILER_SIGNATURE_PRESENT') onChange({ type: 'RETAILER_SIGNATURE_PRESENT' });
          else onChange({ type: 'CUSTOM_PREDICATE', description: '' });
        }}
      >
        {CONDITION_TYPES.map((ct) => <option key={ct} value={ct}>{ct.replace(/_/g, ' ')}</option>)}
      </select>

      <div className="flex-1 min-w-0">
        {cond.type === 'SKU_PRESENCE' && (
          <input
            className={`${inputCls} w-full`}
            placeholder={t('admin.schemes.madLibs.skuPlaceholder', { defaultValue: 'OREO-137G, CDM-90G (comma-separated)' })}
            value={cond.targetSkus.join(', ')}
            onChange={(e) => onChange({ ...cond, targetSkus: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
          />
        )}
        {cond.type === 'DISPLAY_EVIDENCE' && (
          <select className={`${inputCls}`} value={cond.evidenceType} onChange={(e) => onChange({ ...cond, evidenceType: e.target.value as 'PHOTO' | 'VIDEO' })}>
            <option value="PHOTO">PHOTO</option>
            <option value="VIDEO">VIDEO</option>
          </select>
        )}
        {cond.type === 'DOCUMENT_MATCH' && (
          <select className={`${inputCls}`} value={cond.docType} onChange={(e) => onChange({ ...cond, docType: e.target.value as 'INVOICE' | 'RECEIPT' | 'FAKTUR_PAJAK' })}>
            <option value="INVOICE">INVOICE</option>
            <option value="RECEIPT">RECEIPT</option>
            <option value="FAKTUR_PAJAK">FAKTUR PAJAK</option>
          </select>
        )}
        {cond.type === 'MIN_FACINGS' && (
          <input type="number" className={`${inputCls} w-24`} value={cond.count} min={1} onChange={(e) => onChange({ ...cond, count: Number(e.target.value) })} />
        )}
        {cond.type === 'DURATION_HOURS' && (
          <input type="number" className={`${inputCls} w-24`} value={cond.minHours} min={0.5} step={0.5} onChange={(e) => onChange({ ...cond, minHours: Number(e.target.value) })} />
        )}
        {cond.type === 'LISTING_WEEKS' && (
          <input type="number" className={`${inputCls} w-24`} value={cond.minWeeks} min={1} onChange={(e) => onChange({ ...cond, minWeeks: Number(e.target.value) })} />
        )}
        {cond.type === 'VOLUME_THRESHOLD' && (
          <div className="flex gap-2">
            <input type="number" className={`${inputCls} w-24`} value={cond.minVolume} min={0} onChange={(e) => onChange({ ...cond, minVolume: Number(e.target.value) })} />
            <select className={`${inputCls}`} value={cond.uom} onChange={(e) => onChange({ ...cond, uom: e.target.value as 'CASES' | 'UNITS' })}>
              <option value="CASES">CASES</option>
              <option value="UNITS">UNITS</option>
            </select>
          </div>
        )}
        {cond.type === 'RETAILER_SIGNATURE_PRESENT' && (
          <span className="text-xs text-slate-500 italic">Retailer must have signed proof of display</span>
        )}
        {cond.type === 'CUSTOM_PREDICATE' && (
          <input
            className={`${inputCls} w-full`}
            placeholder="Describe the custom condition..."
            value={cond.description}
            onChange={(e) => onChange({ ...cond, description: e.target.value })}
          />
        )}
      </div>

      <button onClick={onRemove} className="text-slate-400 hover:text-danger shrink-0 mt-0.5" title="Remove">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
}

// ── Tier row editor ───────────────────────────────────────────────────────────

export type TierEntry = { minThreshold: number; maxThreshold: number | null; rewardAmount: number; rewardType: 'FLAT_AMOUNT' | 'PERCENTAGE' };

export function TierRow({ tier, onChange, onRemove }: {
  tier: TierEntry;
  onChange: (t: TierEntry) => void;
  onRemove: () => void;
}) {
  const inputCls = 'border border-hairline rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-copper';
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input type="number" className={`${inputCls} w-24`} placeholder="Min" value={tier.minThreshold} min={0}
        onChange={(e) => onChange({ ...tier, minThreshold: Number(e.target.value) })} />
      <span className="text-xs text-slate-400">–</span>
      <input type="number" className={`${inputCls} w-24`} placeholder="Max (blank=∞)" value={tier.maxThreshold ?? ''}
        onChange={(e) => onChange({ ...tier, maxThreshold: e.target.value ? Number(e.target.value) : null })} />
      <input type="number" className={`${inputCls} w-24`} placeholder="Reward" value={tier.rewardAmount}
        onChange={(e) => onChange({ ...tier, rewardAmount: Number(e.target.value) })} />
      <select className={`${inputCls}`} value={tier.rewardType} onChange={(e) => onChange({ ...tier, rewardType: e.target.value as 'FLAT_AMOUNT' | 'PERCENTAGE' })}>
        <option value="FLAT_AMOUNT">IDR</option>
        <option value="PERCENTAGE">%</option>
      </select>
      <button onClick={onRemove} className="text-slate-400 hover:text-danger">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  );
}

// ── Mad-Libs Editor ───────────────────────────────────────────────────────────

export function MadLibsEditor({ mechanic, onChange, readOnly }: {
  mechanic: SchemeMechanic;
  onChange: (next: SchemeMechanic) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const p = mechanic.payout;
  const inputCls = `border rounded px-2 py-0.5 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-copper bg-copper/5 border-copper/30 ${readOnly ? 'opacity-60 cursor-not-allowed' : ''}`;
  const selectCls = `${inputCls} cursor-pointer`;

  function updatePayout(key: string, val: unknown) {
    onChange({ ...mechanic, payout: { ...mechanic.payout, [key]: val } as PayoutDefinition });
  }

  function updateEligibility(conditions: ConditionGroup['conditions']) {
    onChange({ ...mechanic, eligibility: { ...(mechanic.eligibility ?? { logicalOperator: 'AND' as const }), conditions } });
  }

  function updateMeasurementWindow(key: string, val: unknown) {
    onChange({ ...mechanic, measurementWindow: { ...mechanic.measurementWindow, [key]: val } });
  }

  function updateBaselineUnit(val: string) {
    onChange({ ...mechanic, baselineUnit: val as SchemeMechanic['baselineUnit'] });
  }

  const BASELINE_OPTIONS = ['OUTLET', 'SKU_OUTLET', 'INVOICE', 'VOLUME_CASE', 'SALES_VALUE', 'SALES_REP', 'EVENT'];
  const BASELINE_LABELS: Record<string, string> = {
    OUTLET: 'Outlet', SKU_OUTLET: 'SKU×Outlet', INVOICE: 'Invoice Line', VOLUME_CASE: 'Case',
    SALES_VALUE: 'Sales Value', SALES_REP: 'Sales Rep', EVENT: 'Event',
  };

  return (
    <div className="space-y-5">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {t('admin.schemes.madLibs.title', { defaultValue: '2. Define the Payout Rule' })}
      </h3>

      {/* Measurement window */}
      <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
        <span>Measure</span>
        <select disabled={readOnly} className={selectCls} value={mechanic.baselineUnit} onChange={(e) => updateBaselineUnit(e.target.value)}>
          {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b] ?? b}</option>)}
        </select>
        <span>from</span>
        <input disabled={readOnly} type="date" className={inputCls} value={mechanic.measurementWindow.startDate ?? ''}
          onChange={(e) => updateMeasurementWindow('startDate', e.target.value)} />
        <span>to</span>
        <input disabled={readOnly} type="date" className={inputCls} value={mechanic.measurementWindow.endDate ?? ''}
          onChange={(e) => updateMeasurementWindow('endDate', e.target.value)} />
        <select disabled={readOnly} className={selectCls} value={mechanic.measurementWindow.frequency ?? 'MONTHLY'}
          onChange={(e) => updateMeasurementWindow('frequency', e.target.value)}>
          <option value="MONTHLY">monthly</option>
          <option value="QUARTERLY">quarterly</option>
          <option value="ONE_TIME">one-time</option>
        </select>
      </div>

      {/* Payout sentence */}
      {p.type === 'FIXED' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Pay a flat amount of</span>
          <span className="text-slate-500 text-xs">Rp</span>
          <input disabled={readOnly} type="number" min={0} step={10000} className={`${inputCls} w-36 font-mono`}
            value={p.amount} onChange={(e) => updatePayout('amount', Number(e.target.value))} />
          <span>per</span>
          <select disabled={readOnly} className={selectCls} value={mechanic.baselineUnit} onChange={(e) => updateBaselineUnit(e.target.value)}>
            {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b] ?? b}</option>)}
          </select>
          <span>if conditions below are met.</span>
        </div>
      )}

      {p.type === 'LINEAR' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Pay</span>
          <span className="text-slate-500 text-xs">Rp</span>
          <input disabled={readOnly} type="number" min={0} step={1000} className={`${inputCls} w-32 font-mono`}
            value={p.perUnitAmount} onChange={(e) => updatePayout('perUnitAmount', Number(e.target.value))} />
          <span>per</span>
          <select disabled={readOnly} className={selectCls} value={mechanic.baselineUnit} onChange={(e) => updateBaselineUnit(e.target.value)}>
            {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b] ?? b}</option>)}
          </select>
          {p.cap !== undefined && (
            <>
              <span>, capped at Rp</span>
              <input disabled={readOnly} type="number" min={0} step={1000000} className={`${inputCls} w-36 font-mono`}
                value={p.cap ?? 0} onChange={(e) => updatePayout('cap', Number(e.target.value))} />
            </>
          )}
          <span>.</span>
        </div>
      )}

      {p.type === 'TIERED' && (
        <div className="space-y-3">
          <div className="text-sm text-slate-700 flex flex-wrap items-center gap-1.5">
            <span>Evaluate</span>
            <select disabled={readOnly} className={selectCls} value={p.evaluationMetric}
              onChange={(e) => updatePayout('evaluationMetric', e.target.value)}>
              <option value="VOLUME">Volume (cases)</option>
              <option value="VALUE">Sales Value (IDR)</option>
              <option value="OUTLET_COUNT">Outlet Count</option>
            </select>
            <span>and pay per tier —</span>
            <select disabled={readOnly} className={selectCls} value={p.tierLogic}
              onChange={(e) => updatePayout('tierLogic', e.target.value)}>
              <option value="HIGHEST_QUALIFIED">Highest Tier Reached</option>
              <option value="CUMULATIVE">Cumulative (all tiers)</option>
            </select>
            <span>logic.</span>
          </div>
          <div className="space-y-1.5 pl-2 border-l-2 border-copper/30">
            {p.tiers.map((tier, i) => (
              <TierRow
                key={i}
                tier={tier}
                onChange={(updated) => {
                  const newTiers = [...p.tiers];
                  newTiers[i] = updated;
                  updatePayout('tiers', newTiers);
                }}
                onRemove={() => updatePayout('tiers', p.tiers.filter((_, j) => j !== i))}
              />
            ))}
            {!readOnly && (
              <button
                onClick={() => updatePayout('tiers', [...p.tiers, { minThreshold: 0, maxThreshold: null, rewardAmount: 0, rewardType: 'PERCENTAGE' as const }])}
                className="text-xs text-copper hover:underline"
              >
                + Add tier
              </button>
            )}
          </div>
        </div>
      )}

      {p.type === 'REIMBURSEMENT' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Reimburse</span>
          <input disabled={readOnly} type="number" min={0} max={100} step={5} className={`${inputCls} w-16 font-mono`}
            value={p.reimbursementPercent} onChange={(e) => updatePayout('reimbursementPercent', Number(e.target.value))} />
          <span>% of receipted expense per</span>
          <select disabled={readOnly} className={selectCls} value={mechanic.baselineUnit} onChange={(e) => updateBaselineUnit(e.target.value)}>
            {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b] ?? b}</option>)}
          </select>
          {p.maxPerBaseline != null && (
            <>
              <span>, capped at Rp</span>
              <input disabled={readOnly} type="number" min={0} step={100000} className={`${inputCls} w-32 font-mono`}
                value={p.maxPerBaseline} onChange={(e) => updatePayout('maxPerBaseline', Number(e.target.value))} />
            </>
          )}
          <span>. Vendor invoice required:
            <input disabled={readOnly} type="checkbox" className="ml-1 accent-copper"
              checked={p.requiresVendorInvoice}
              onChange={(e) => updatePayout('requiresVendorInvoice', e.target.checked)} />
          </span>
        </div>
      )}

      {p.type === 'FORMULA' && (
        <div className="space-y-2">
          <div className="text-sm text-slate-700">Pay according to formula:</div>
          <textarea disabled={readOnly} rows={2}
            className="w-full border border-hairline rounded px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-copper"
            value={p.expression}
            onChange={(e) => updatePayout('expression', e.target.value)}
            placeholder="(eligible_cases × 15000) − (returns × 5000)"
          />
          <div className="text-xs text-slate-500">Variables (comma-sep):
            <input disabled={readOnly} className="ml-2 border border-hairline rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-copper"
              value={p.variables.join(', ')}
              onChange={(e) => updatePayout('variables', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
            />
          </div>
        </div>
      )}

      {p.type === 'SHELF_RENTAL' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Pay</span>
          <span className="text-slate-500 text-xs">Rp</span>
          <input disabled={readOnly} type="number" min={0} step={100000} className={`${inputCls} w-36 font-mono`}
            value={p.monthlyRate} onChange={(e) => updatePayout('monthlyRate', Number(e.target.value))} />
          <span>per month for</span>
          <select disabled={readOnly} className={selectCls} value={p.spaceType}
            onChange={(e) => updatePayout('spaceType', e.target.value)}>
            <option value="TOP_SHELF">Top Shelf</option>
            <option value="END_CAP">End Cap</option>
            <option value="COUNTER_TOP">Counter Top</option>
            <option value="CUSTOM">Custom</option>
          </select>
          <span>space. Exclusive:</span>
          <select disabled={readOnly} className={selectCls} value={String(p.monopolyClause)}
            onChange={(e) => updatePayout('monopolyClause', e.target.value === 'true')}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      )}

      {p.type === 'FREE_GOODS_RATIO' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Provide</span>
          <input disabled={readOnly} type="number" min={1} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.freeQty} onChange={(e) => updatePayout('freeQty', Number(e.target.value))} />
          <span>cartons free of SKU</span>
          <input disabled={readOnly} type="text" className={`${inputCls} w-28`}
            value={p.sku} onChange={(e) => updatePayout('sku', e.target.value)} placeholder="OREO-137G" />
          <span>per</span>
          <input disabled={readOnly} type="number" min={1} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.orderedQty} onChange={(e) => updatePayout('orderedQty', Number(e.target.value))} />
          <span>ordered. Unit cost Rp</span>
          <input disabled={readOnly} type="number" min={0} step={1000} className={`${inputCls} w-28 font-mono`}
            value={p.skuUnitCost} onChange={(e) => updatePayout('skuUnitCost', Number(e.target.value))} />
          <span>. Anti-resale:</span>
          <select disabled={readOnly} className={selectCls} value={String(p.antiResaleClause)}
            onChange={(e) => updatePayout('antiResaleClause', e.target.value === 'true')}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      )}

      {p.type === 'TRANSACTION_INSTANT_DISCOUNT' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Apply</span>
          <input disabled={readOnly} type="number" min={0} step={p.discountType === 'PERCENT' ? 1 : 10000} className={`${inputCls} w-24 font-mono`}
            value={p.amount} onChange={(e) => updatePayout('amount', Number(e.target.value))} />
          <select disabled={readOnly} className={selectCls} value={p.discountType}
            onChange={(e) => updatePayout('discountType', e.target.value)}>
            <option value="PERCENT">%</option>
            <option value="FLAT">IDR</option>
          </select>
          <span>discount on transactions matching</span>
          <input disabled={readOnly} type="text" className={`${inputCls} flex-1 min-w-40`}
            value={p.triggerCondition} onChange={(e) => updatePayout('triggerCondition', e.target.value)}
            placeholder="e.g. 3+ cases OREO" />
          <span>for up to</span>
          <input disabled={readOnly} type="number" min={1} max={90} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.maxDurationDays} onChange={(e) => updatePayout('maxDurationDays', Number(e.target.value))} />
          <span>days.</span>
        </div>
      )}

      {p.type === 'LOYALTY_POINT_ACCRUAL' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Award</span>
          <input disabled={readOnly} type="number" min={1} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.pointsPerCurrencyUnit} onChange={(e) => updatePayout('pointsPerCurrencyUnit', Number(e.target.value))} />
          <span>pt(s) per Rp</span>
          <input disabled={readOnly} type="number" min={1000} step={1000} className={`${inputCls} w-24 font-mono`}
            value={p.currencyUnit} onChange={(e) => updatePayout('currencyUnit', Number(e.target.value))} />
          <span>spent. 1 pt = Rp</span>
          <input disabled={readOnly} type="number" min={1} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.pointValueCurrency} onChange={(e) => updatePayout('pointValueCurrency', Number(e.target.value))} />
          <span>. Valid</span>
          <input disabled={readOnly} type="number" min={1} max={24} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.validityMonths} onChange={(e) => updatePayout('validityMonths', Number(e.target.value))} />
          <span>months.</span>
        </div>
      )}

      {p.type === 'PAYMENT_TERMS_EXTENSION' && (
        <div className="text-sm text-slate-700 leading-relaxed flex flex-wrap items-center gap-1.5">
          <span>Extend payment from</span>
          <input disabled={readOnly} type="number" min={1} max={365} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.baseTermDays} onChange={(e) => updatePayout('baseTermDays', Number(e.target.value))} />
          <span>days to</span>
          <input disabled={readOnly} type="number" min={1} max={365} step={1} className={`${inputCls} w-16 font-mono`}
            value={p.extensionDays} onChange={(e) => updatePayout('extensionDays', Number(e.target.value))} />
          <span>days for</span>
          <input disabled={readOnly} type="text" className={`${inputCls} flex-1 min-w-40`}
            value={p.qualifyingCondition} onChange={(e) => updatePayout('qualifyingCondition', e.target.value)}
            placeholder="qualifying condition" />
          <span>.</span>
        </div>
      )}

      {p.type === 'WEIGHTED_KPI_BOUNTY' && (
        <div className="space-y-4">
          {/* Base payout sentence */}
          <div className="text-sm text-slate-700 flex flex-wrap items-center gap-1.5">
            <span>Pay</span>
            <span className="text-slate-500 text-xs">Rp</span>
            <input disabled={readOnly} type="number" min={0} step={100000} className={`${inputCls} w-36 font-mono`}
              value={p.basePayout} onChange={(e) => updatePayout('basePayout', Number(e.target.value))} />
            <span>base per</span>
            <select disabled={readOnly} className={selectCls} value={mechanic.baselineUnit} onChange={(e) => updateBaselineUnit(e.target.value)}>
              {BASELINE_OPTIONS.map((b) => <option key={b} value={b}>{BASELINE_LABELS[b] ?? b}</option>)}
            </select>
            <span>based on weighted KPI scoring:</span>
          </div>

          {/* Metrics */}
          <div className="space-y-1.5 pl-2 border-l-2 border-copper/30">
            {p.metrics.map((metric, i) => (
              <div key={i} className="flex items-center gap-2 flex-wrap">
                <input disabled={readOnly} type="text" className={`${inputCls} w-36`} placeholder="Metric name"
                  value={metric.name}
                  onChange={(e) => {
                    const updated = [...p.metrics];
                    updated[i] = { ...metric, name: e.target.value };
                    updatePayout('metrics', updated);
                  }} />
                <span className="text-xs text-slate-500">weight</span>
                <input disabled={readOnly} type="number" min={0} max={100} step={5} className={`${inputCls} w-16 font-mono`}
                  value={Math.round(metric.weighting * 100)}
                  onChange={(e) => {
                    const updated = [...p.metrics];
                    updated[i] = { ...metric, weighting: Number(e.target.value) / 100 };
                    updatePayout('metrics', updated);
                  }} />
                <span className="text-xs text-slate-500">%</span>
                <select disabled={readOnly} className={selectCls} value={metric.measurementType}
                  onChange={(e) => {
                    const updated = [...p.metrics];
                    updated[i] = { ...metric, measurementType: e.target.value as 'PERCENT_OF_TARGET' | 'ABSOLUTE' };
                    updatePayout('metrics', updated);
                  }}>
                  <option value="PERCENT_OF_TARGET">% of Target</option>
                  <option value="ABSOLUTE">Absolute</option>
                </select>
                {!readOnly && (
                  <button onClick={() => updatePayout('metrics', p.metrics.filter((_, j) => j !== i))}
                    className="text-slate-400 hover:text-danger">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                )}
              </div>
            ))}
            {!readOnly && (
              <button
                onClick={() => updatePayout('metrics', [...p.metrics, { name: '', weighting: 0, measurementType: 'PERCENT_OF_TARGET' as const }])}
                className="text-xs text-copper hover:underline">
                + Add metric
              </button>
            )}
            {(() => {
              const sum = p.metrics.reduce((s, m) => s + m.weighting, 0);
              return Math.abs(sum - 1) > 0.001 ? (
                <p className="text-xs text-amber-600">⚠ Weights sum to {Math.round(sum * 100)}% — must equal 100%</p>
              ) : null;
            })()}
          </div>

          {/* Gatekeeper */}
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Gatekeeper</div>
            {p.gatekeeper ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-700">If</span>
                <input disabled={readOnly} type="text" className={`${inputCls} w-36`} placeholder="Metric name"
                  value={p.gatekeeper.metricName}
                  onChange={(e) => updatePayout('gatekeeper', { ...p.gatekeeper!, metricName: e.target.value })} />
                <span className="text-sm text-slate-700">below</span>
                <input disabled={readOnly} type="number" min={0} max={100} step={5} className={`${inputCls} w-16 font-mono`}
                  value={p.gatekeeper.minimumThresholdPercent}
                  onChange={(e) => updatePayout('gatekeeper', { ...p.gatekeeper!, minimumThresholdPercent: Number(e.target.value) })} />
                <span className="text-sm text-slate-700">% → pay Rp 0.</span>
                {!readOnly && (
                  <button onClick={() => updatePayout('gatekeeper', null)} className="text-xs text-slate-400 hover:text-danger ml-1">Remove</button>
                )}
              </div>
            ) : (
              !readOnly && (
                <button
                  onClick={() => updatePayout('gatekeeper', { metricName: p.metrics[0]?.name ?? '', minimumThresholdPercent: 85 })}
                  className="text-xs text-copper hover:underline">
                  + Add gatekeeper
                </button>
              )
            )}
          </div>

          {/* Multiplier curve */}
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Multiplier Curve</div>
            <div className="space-y-1 pl-2 border-l-2 border-copper/30">
              {p.multiplierCurve.map((tier, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <input disabled={readOnly} type="number" className={`${inputCls} w-20 font-mono`} placeholder="Min %"
                    value={tier.achievementPercentMin}
                    onChange={(e) => {
                      const updated = [...p.multiplierCurve];
                      updated[i] = { ...tier, achievementPercentMin: Number(e.target.value) };
                      updatePayout('multiplierCurve', updated);
                    }} />
                  <span className="text-xs text-slate-400">–</span>
                  <input disabled={readOnly} type="number" className={`${inputCls} w-20 font-mono`} placeholder="Max % (∞)"
                    value={tier.achievementPercentMax ?? ''}
                    onChange={(e) => {
                      const updated = [...p.multiplierCurve];
                      updated[i] = { ...tier, achievementPercentMax: e.target.value ? Number(e.target.value) : null };
                      updatePayout('multiplierCurve', updated);
                    }} />
                  <span className="text-xs text-slate-400">→</span>
                  <input disabled={readOnly} type="text" className={`${inputCls} w-24 font-mono`} placeholder="1.0 or LINEAR"
                    value={String(tier.multiplier)}
                    onChange={(e) => {
                      const updated = [...p.multiplierCurve];
                      const val = e.target.value.trim();
                      updated[i] = { ...tier, multiplier: val === 'LINEAR' ? 'LINEAR' : (Number(val) || 0) };
                      updatePayout('multiplierCurve', updated);
                    }} />
                  <span className="text-xs text-slate-500">×</span>
                  {!readOnly && (
                    <button onClick={() => updatePayout('multiplierCurve', p.multiplierCurve.filter((_, j) => j !== i))}
                      className="text-slate-400 hover:text-danger">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  )}
                </div>
              ))}
              {!readOnly && (
                <button
                  onClick={() => updatePayout('multiplierCurve', [...p.multiplierCurve, { achievementPercentMin: 100, achievementPercentMax: null, multiplier: 1 }])}
                  className="text-xs text-copper hover:underline">
                  + Add tier
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Eligibility conditions */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
          {t('admin.schemes.madLibs.eligibilityTitle', { defaultValue: 'Eligibility Conditions (AND)' })}
        </div>
        <div className="space-y-1.5">
          {(mechanic.eligibility?.conditions ?? []).map((cond, i) => (
            <ConditionRow
              key={i}
              cond={cond as Condition}
              onChange={(updated) => {
                const updated2 = [...(mechanic.eligibility?.conditions ?? [])];
                updated2[i] = updated;
                updateEligibility(updated2);
              }}
              onRemove={() => updateEligibility((mechanic.eligibility?.conditions ?? []).filter((_, j) => j !== i))}
            />
          ))}
        </div>
        {!readOnly && (
          <button
            onClick={() => updateEligibility([...(mechanic.eligibility?.conditions ?? []), { type: 'CUSTOM_PREDICATE', description: '' }])}
            className="mt-2 text-xs text-copper hover:underline"
          >
            + {t('admin.schemes.addRule.menu.eligibilityCondition', { defaultValue: 'Add condition' })}
          </button>
        )}
        {(mechanic.eligibility?.conditions ?? []).length === 0 && (
          <p className="text-xs text-danger mt-1">⚠ {t('admin.schemes.madLibs.eligibilityRequired', { defaultValue: 'Specify at least one eligibility condition' })}</p>
        )}
      </div>
    </div>
  );
}

// ── External Verification panel ───────────────────────────────────────────────

export function ExternalVerificationPanel({ verifications, onChange, readOnly }: {
  verifications: ExternalVerification[];
  onChange: (v: ExternalVerification[]) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const inputCls = 'border border-hairline rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-copper';

  return (
    <div className="space-y-2">
      {verifications.map((v, i) => (
        <div key={i} className="bg-amber-50 border border-amber-200 rounded p-3 text-xs space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <span className="font-semibold text-amber-800">⚙ {t('admin.schemes.addRule.externalVerification.label', { defaultValue: 'External Verification' })}</span>
            {!readOnly && (
              <button onClick={() => onChange(verifications.filter((_, j) => j !== i))} className="text-slate-400 hover:text-danger">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Constraint</label>
              <input disabled={readOnly} className={`${inputCls} w-full mt-0.5`} value={v.constraintName}
                onChange={(e) => { const u = [...verifications]; u[i] = { ...v, constraintName: e.target.value }; onChange(u); }} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-500">Route to</label>
              <select disabled={readOnly} className={`${inputCls} w-full mt-0.5`} value={v.routingRole ?? 'FINANCE_MANAGER'}
                onChange={(e) => { const u = [...verifications]; u[i] = { ...v, routingRole: e.target.value as ExternalVerification['routingRole'] }; onChange(u); }}>
                <option value="FINANCE_MANAGER">Finance Manager</option>
                <option value="COMMERCIAL_HEAD">Commercial Head</option>
                <option value="BRAND_DIRECTOR">Brand Director</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Description</label>
            <input disabled={readOnly} className={`${inputCls} w-full mt-0.5`} value={v.description}
              onChange={(e) => { const u = [...verifications]; u[i] = { ...v, description: e.target.value }; onChange(u); }} />
          </div>
        </div>
      ))}
      {!readOnly && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => onChange([
              ...verifications,
              {
                constraintName: 'NO_OUTSTANDING_DUES',
                description: t('admin.schemes.addRule.financePresetDesc', { defaultValue: 'Distributor must have no overdue invoices > 60 days at time of payout' }),
                resolutionMethod: 'MANUAL_APPROVAL',
                routingRole: 'FINANCE_MANAGER',
              },
            ])}
            className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded px-2 py-1 hover:bg-amber-100 transition-colors"
          >
            + {t('admin.schemes.addRule.financePreset', { defaultValue: 'Finance clearance (no outstanding dues)' })}
          </button>
          <button
            onClick={() => onChange([...verifications, { constraintName: '', description: '', resolutionMethod: 'MANUAL_APPROVAL', routingRole: 'FINANCE_MANAGER' }])}
            className="text-xs text-copper hover:underline"
          >
            + {t('admin.schemes.addRule.menu.externalVerification', { defaultValue: 'Custom verification' })}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Tax block ─────────────────────────────────────────────────────────────────

export function TaxBlock({ taxTreatment, pphCategory, withholdingPercent, onChange, readOnly }: {
  taxTreatment: string;
  pphCategory: string;
  withholdingPercent: string;
  onChange: (f: string, v: string) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const inputCls = `border border-hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-copper ${readOnly ? 'opacity-60' : ''}`;
  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {t('admin.schemes.tax.title', { defaultValue: 'Tax Treatment' })}
      </h3>
      <div className="flex gap-4 flex-wrap">
        {(['GROSS_UP', 'NET_DEDUCT', 'EXEMPT'] as const).map((val) => (
          <label key={val} className="flex items-center gap-2 cursor-pointer">
            <input type="radio" disabled={readOnly} className="accent-copper" name="taxTreatment" value={val}
              checked={taxTreatment === val} onChange={() => {
                onChange('taxTreatment', val);
                if (val === 'NET_DEDUCT') { onChange('pphCategory', 'PPH_23_SERVICE'); onChange('withholdingPercent', '2.00'); }
                else if (val === 'EXEMPT') { onChange('pphCategory', 'NONE'); onChange('withholdingPercent', '0'); }
              }} />
            <span className="text-sm">{t(`admin.schemes.tax.treatment.${val.toLowerCase().replace(/_/g, '')}`, { defaultValue: val.replace(/_/g, ' ') })}</span>
          </label>
        ))}
      </div>
      {taxTreatment !== 'EXEMPT' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.tax.pphCategory', { defaultValue: 'PPh Category' })}</label>
            <select disabled={readOnly} className={`${inputCls} w-full`} value={pphCategory}
              onChange={(e) => {
                onChange('pphCategory', e.target.value);
                onChange('withholdingPercent', e.target.value === 'PPH_23_SERVICE' ? '2.00' : e.target.value === 'PPH_4_2_FINAL' ? '0.50' : '0');
              }}>
              <option value="PPH_23_SERVICE">PPh 23 Service (2%)</option>
              <option value="PPH_4_2_FINAL">PPh 4(2) Final (0.5%)</option>
              <option value="NONE">None</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.tax.withholdingPercent', { defaultValue: 'Withholding %' })}</label>
            <input disabled={readOnly} type="number" min={0} max={100} step={0.5} className={`${inputCls} w-full font-mono`}
              value={withholdingPercent} onChange={(e) => onChange('withholdingPercent', e.target.value)} />
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400">
        {t('admin.schemes.tax.helper', { defaultValue: 'For trade spend treated as service rendered (most common), use NET_DEDUCT + PPH_23_SERVICE at 2%.' })}
      </p>
    </div>
  );
}

// ── Fund Allocation block ─────────────────────────────────────────────────────

export function FundAllocationBlock({ glAccountCode, sapWbsElement, budgetCapAmount, onChange, readOnly }: {
  glAccountCode: string;
  sapWbsElement: string;
  budgetCapAmount: string;
  onChange: (f: string, v: string) => void;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const inputCls = `border border-hairline rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-copper ${readOnly ? 'opacity-60' : ''}`;
  return (
    <div className="space-y-4">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
        {t('admin.schemes.fund.title', { defaultValue: 'Fund Allocation' })}
      </h3>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.fund.glAccountLabel', { defaultValue: 'GL Account Code' })}</label>
          <input disabled={readOnly} type="text" className={`${inputCls} w-full font-mono text-xs`} value={glAccountCode}
            onChange={(e) => onChange('glAccountCode', e.target.value)}
            placeholder="TRADE_SPEND.OREO.Q2_2026.VISIBILITY" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.fund.sapWbsElement', { defaultValue: 'SAP WBS Element (optional)' })}</label>
          <input disabled={readOnly} type="text" className={`${inputCls} w-full font-mono text-xs`} value={sapWbsElement}
            onChange={(e) => onChange('sapWbsElement', e.target.value)}
            placeholder="WBS-MDLZ-Q2-VIS-001" />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">{t('admin.schemes.fund.budgetCapLabel', { defaultValue: 'Budget Cap (IDR)' })}</label>
        <input disabled={readOnly} type="number" min={0} step={1000000} className={`${inputCls} w-full font-mono`}
          value={budgetCapAmount} onChange={(e) => onChange('budgetCapAmount', e.target.value)}
          placeholder="0 = no cap" />
        <p className="text-xs text-slate-400 mt-1">
          {t('admin.schemes.fund.budgetHelper', { defaultValue: 'Approved claims will live-decrement this cap. Claims that would exceed it are rejected.' })}
        </p>
      </div>
    </div>
  );
}
